import { CardRow } from 'components/CardRow';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { filterArrayEmpty } from 'lib/riskbase';
import React from 'react';
import {
  selectAseSetupLibraryQuestions,
  selectRemoteCustomers,
  selectRemoteFieldFields,
  selectRemoteFieldGroups,
  selectRemoteFieldSectionIdsByOrganisation,
  selectRemoteFieldSections,
} from 'store/remote/selectors';
import { AssessmentTemplate } from 'store/remote/types';
import { AssessmentQuestionType, UID } from 'types/types';

type Props = {
  config: AssessmentTemplate['fieldsConfig'][string] | undefined;
  organisationId: UID;
  onPress: () => void;
};

export const FieldsListItem: React.FC<Props> = React.memo(
  ({ organisationId, config, onPress }) => {
    const customers = useSelector(selectRemoteCustomers);
    const organisationHasSectionIds = useSelector(
      selectRemoteFieldSectionIdsByOrganisation,
    );
    const questions = useSelector(selectAseSetupLibraryQuestions);
    const sections = useSelector(selectRemoteFieldSections);
    const groups = useSelector(selectRemoteFieldGroups);
    const fields = useSelector(selectRemoteFieldFields);
    let groupsCount = 0;
    let fieldsViewableCount = 0;
    let fieldsEditableCount = 0;
    let checklistCount: number = 0;
    let questionCount: number = 0;

    const customer = customers?.[organisationId];
    const chosen =
      config && organisationHasSectionIds && sections && groups && fields
        ? (organisationHasSectionIds[organisationId] || []).reduce<
            { section: string; groups: string[] }[]
          >((acc, sectionId) => {
            const section = sections[sectionId];
            if (section) {
              const chosenGroups: string[] = [];
              section.groupIds.forEach((groupId) => {
                const group = groups[groupId];
                const groupConfig = config.groups[groupId];
                if (group && groupConfig) {
                  groupsCount += 1;
                  chosenGroups.push(group.name);
                  groupConfig.questionIds.forEach((questionId) => {
                    if (
                      questions[questionId]?.type ===
                      AssessmentQuestionType.checklist
                    ) {
                      checklistCount += 1;
                    } else {
                      questionCount += 1;
                    }
                  });
                  group.fieldIds.forEach((fieldId) => {
                    const field = fields[fieldId];
                    const fieldConfig = groupConfig.fields[fieldId];
                    if (
                      field &&
                      (!!fieldConfig && fieldConfig?.include !== null
                        ? fieldConfig?.include
                        : groupConfig.fieldsInclude)
                    ) {
                      if (
                        !!fieldConfig && fieldConfig?.editable !== null
                          ? fieldConfig?.editable
                          : groupConfig.fieldsEditable
                      ) {
                        fieldsEditableCount += 1;
                      } else {
                        fieldsViewableCount += 1;
                      }
                    }
                  });
                }
              });
              if (chosenGroups.length) {
                acc.push({
                  section: section.name,
                  groups: chosenGroups,
                });
              }
            }
            return acc;
          }, [])
        : [];

    return (
      <CardRow
        navigationType="input-radio"
        onPress={onPress}
        labelAlignTop
        labelNumberOfLines={2}
        label={[
          pluralise(groupsCount, '1 Group', '{x} Groups', 'No Groups'),
          pluralise(
            fieldsViewableCount,
            '1 Viewable Field',
            '{x} Viewable Fields',
            '',
          ),
          pluralise(
            fieldsEditableCount,
            '1 Editable Field',
            '{x} Editable Fields',
            '',
          ),
          pluralise(checklistCount, '1 Checklist', '{x} Checklists', ''),
          pluralise(questionCount, '1 Question', '{x} Questions', ''),
        ]
          .filter(filterArrayEmpty)
          .join(', ')}
        label2={chosen
          .map((section) => `${section.section} (${section.groups.join(', ')})`)
          .join(', ')}
        value={
          customer ? `Customised for ${customer.name}` : 'Default Field Setup'
        }
        childrenStyle={{
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      />
    );
  },
);
